import { useState } from "react";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  preferred_date: "",
  event_type: "juhlat",
  guests: "",
  message: "",
  website: "",
};

const inputClass =
  "w-full rounded-xl border border-[#8A9B87]/25 bg-[#F7F4EE]/60 px-4 py-3 text-base text-[#2E2E2C] outline-none transition-[border-color,box-shadow] duration-200 focus:border-[#8A9B87] focus:ring-1 focus:ring-[#8A9B87]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm text-[#2E2E2C]/70">{label}</span>
    {children}
  </label>
);

export const BookingForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/inquiries`, form);
      setStatus("done");
      setForm(EMPTY);
    } catch (err) {
      setStatus("idle");
      setError("Lähetys ei onnistunut. Yritä uudelleen tai soita 050 341 6253.");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid="booking-success"
        className="mx-auto max-w-2xl rounded-3xl border border-[#8A9B87]/20 bg-white p-10 sm:p-14 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      >
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#8A9B87]/15 text-[#8A9B87]">
          <Check size={26} strokeWidth={1.8} />
        </span>
        <p className="mt-6 font-hand text-3xl text-[#8A9B87]">Kiitos!</p>
        <p className="mt-2 text-base text-[#2E2E2C]/75">Palaamme sinulle mahdollisimman pian.</p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          data-testid="booking-new-inquiry"
          className="mt-8 text-sm text-[#A9805B] underline underline-offset-4 transition-colors hover:text-[#8A9B87]"
        >
          Lähetä uusi kysely
        </button>
      </div>
    );
  }

  return (
    <Reveal>
      <form
        onSubmit={submit}
        data-testid="booking-form"
        className="mx-auto max-w-2xl rounded-3xl border border-[#8A9B87]/15 bg-white p-7 sm:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      >
        <input
          type="text"
          value={form.website}
          onChange={set("website")}
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] h-0 w-0 opacity-0"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Nimi">
            <input required value={form.name} onChange={set("name")} className={inputClass} data-testid="input-name" />
          </Field>
          <Field label="Sähköposti">
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              className={inputClass}
              data-testid="input-email"
            />
          </Field>
          <Field label="Puhelin">
            <input required value={form.phone} onChange={set("phone")} className={inputClass} data-testid="input-phone" />
          </Field>
          <Field label="Toivottu päivä">
            <input
              required
              type="date"
              value={form.preferred_date}
              onChange={set("preferred_date")}
              className={inputClass}
              data-testid="input-date"
            />
          </Field>
          <Field label="Tilaisuuden tyyppi">
            <select value={form.event_type} onChange={set("event_type")} className={inputClass} data-testid="input-event-type">
              <option value="juhlat">Juhlat</option>
              <option value="kokous">Kokous tai koulutus</option>
              <option value="saunailta">Saunailta</option>
              <option value="muu">Muu</option>
            </select>
          </Field>
          <Field label="Henkilömäärä">
            <input
              required
              type="number"
              min="1"
              value={form.guests}
              onChange={set("guests")}
              className={inputClass}
              data-testid="input-guests"
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field label="Viesti">
            <textarea
              rows={4}
              value={form.message}
              onChange={set("message")}
              className={`${inputClass} resize-none`}
              data-testid="input-message"
            />
          </Field>
        </div>

        {error && (
          <p className="mt-5 text-sm text-[#A9805B]" data-testid="booking-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="booking-submit"
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#8A9B87] py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-[#A9805B] disabled:opacity-60"
        >
          {status === "sending" && <Loader2 size={16} className="animate-spin" />}
          {status === "sending" ? "Lähetetään…" : "Lähetä kysely"}
        </button>
      </form>
    </Reveal>
  );
};
